import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  campaignReport,
  cancelCampaign,
  createCampaign,
  createSegment,
  deleteSegment,
  listCampaigns,
  listSegments,
  previewCampaign,
  previewSegment,
  saveVariant,
  scheduleCampaign,
  type CampaignPreview,
  type CampaignReport,
  type CampaignView,
  type SegmentView,
} from './campaigns-api';

/**
 * E-mail marketing (context.md sections 45, 46, 80).
 *
 * The preview is the part that matters on this screen. A campaign's audience is a segment resolved
 * when it is sent, so the number of people it will reach is not obvious from anything on the form —
 * and neither is how many of them are suppressed or have withdrawn consent. Those are shown before
 * the send button does anything, because they are the difference between a campaign and a complaint.
 */

const STATUS_TONE: Record<CampaignView['status'], BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'info',
  SENDING: 'info',
  SENT: 'positive',
  CANCELED: 'warning',
  FAILED: 'danger',
};

type Tab = 'campaigns' | 'segments';

export function CampaignsPage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('campaigns');

  return (
    <div className="view">
      <PageHeader
        title={t('nav.campaigns')}
        description={t('campaigns.description')}
        actions={
          <Link to="/marketing/campanhas/nova" className="button button--primary button--compact">
            + {t('campaigns.createCampaign')}
          </Link>
        }
      />

      <div className="tabs" role="tablist">
        {(['campaigns', 'segments'] as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className="tab"
            onClick={() => setTab(key)}
          >
            {t(`campaigns.tab.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'campaigns' ? <CampaignsTab /> : <SegmentsTab />}
    </div>
  );
}

function CampaignsTab(): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, CampaignPreview>>({});
  const [reports, setReports] = useState<Record<string, CampaignReport>>({});

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [addresses, setAddresses] = useState('');

  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: listCampaigns });
  const segments = useQuery({ queryKey: ['segments'], queryFn: listSegments });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['campaigns'] });
  };

  const recipients = addresses
    .split(/[\n,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  const create = useMutation({
    mutationFn: () =>
      createCampaign({
        name: name.trim(),
        subject: subject.trim(),
        bodyText: body,
        ...(segmentId ? { segmentId } : { recipients }),
      }),
    onSuccess: () => {
      setName('');
      setSubject('');
      setBody('');
      setAddresses('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const preview = useMutation({
    mutationFn: (id: string) => previewCampaign(id),
    onSuccess: (data) => setPreviews((current) => ({ ...current, [data.campaign_id]: data })),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const report = useMutation({
    mutationFn: (id: string) => campaignReport(id),
    onSuccess: (data) => setReports((current) => ({ ...current, [data.campaign_id]: data })),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const send = useMutation({
    mutationFn: (id: string) => scheduleCampaign(id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelCampaign(id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const addVariant = useMutation({
    mutationFn: (campaign: CampaignView) =>
      saveVariant(campaign.id, {
        // The next free letter: A already exists on every campaign.
        label: String.fromCharCode(65 + campaign.variants.length),
        subject: `${campaign.variants[0]?.subject ?? ''} (B)`,
        bodyText: campaign.variants[0]?.body_text ?? '',
      }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<CampaignView>[] = [
    {
      key: 'name',
      header: t('campaigns.campaign'),
      render: (campaign) => (
        <span className="cell-stack">
          <span className="strong">{campaign.name}</span>
          <span className="muted small">{campaign.variants[0]?.subject}</span>
        </span>
      ),
    },
    {
      key: 'audience',
      header: t('campaigns.audience'),
      secondary: true,
      render: (campaign) =>
        campaign.audience_kind === 'SEGMENT'
          ? (segments.data?.segments.find((segment) => segment.id === campaign.segment_id)?.name ??
            t('campaigns.segment'))
          : t('campaigns.list'),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (campaign) => (
        <span className="cell-stack">
          <Badge tone={STATUS_TONE[campaign.status]}>
            {t(`campaigns.state.${campaign.status}`)}
          </Badge>
          {campaign.variants.length > 1 && (
            <span className="muted small">
              {t('campaigns.variantCount').replace('{n}', String(campaign.variants.length))}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'result',
      header: t('campaigns.result'),
      render: (campaign) => {
        const summary = reports[campaign.id];

        if (summary) {
          return (
            <span className="cell-stack">
              <span>
                {t('campaigns.reportLine')
                  .replace('{opened}', String(summary.totals.opened))
                  .replace('{clicked}', String(summary.totals.clicked))
                  .replace('{unsubscribed}', String(summary.totals.unsubscribed))}
              </span>
              {summary.not_reported.length > 0 && (
                <span className="muted small">
                  {t('campaigns.notReported').replace(
                    '{metrics}',
                    summary.not_reported
                      .map((metric) => t(`campaigns.metric.${metric}`))
                      .join(', '),
                  )}
                </span>
              )}
            </span>
          );
        }

        const forecast = previews[campaign.id];

        if (forecast) {
          return (
            <span className="cell-stack">
              <span>{t('campaigns.previewLine').replace('{n}', String(forecast.sendable))}</span>
              {forecast.suppressed + forecast.without_consent > 0 && (
                <span className="muted small">
                  {t('campaigns.previewSkipped')
                    .replace('{suppressed}', String(forecast.suppressed))
                    .replace('{consent}', String(forecast.without_consent))}
                </span>
              )}
            </span>
          );
        }

        return campaign.finished_at
          ? t('campaigns.sentLine')
              .replace('{queued}', String(campaign.queued_count))
              .replace('{total}', String(campaign.recipients_total))
          : '—';
      },
    },
    {
      key: 'when',
      header: t('campaigns.when'),
      align: 'end',
      secondary: true,
      render: (campaign) => formatDateTime(campaign.finished_at ?? campaign.created_at),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (campaign) => (
        <span className="cell-inline">
          {campaign.status === 'DRAFT' && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => addVariant.mutate(campaign)}
            >
              {t('campaigns.addVariant')}
            </button>
          )}

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() =>
              campaign.finished_at ? report.mutate(campaign.id) : preview.mutate(campaign.id)
            }
          >
            {campaign.finished_at ? t('campaigns.report') : t('campaigns.preview')}
          </button>

          {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
            <>
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={send.isPending}
                onClick={() => send.mutate(campaign.id)}
              >
                {t('campaigns.sendNow')}
              </button>

              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(campaign.id)}
              >
                {t('sms.cancel')}
              </button>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <Panel title={t('campaigns.newCampaign')}>
        <div className="form-grid">
          <label className="field">
            <span>{t('campaigns.campaign')}</span>
            <input value={name} maxLength={160} onChange={(event) => setName(event.target.value)} />
          </label>

          <label className="field">
            <span>{t('campaigns.subject')}</span>
            <input
              value={subject}
              maxLength={300}
              onChange={(event) => setSubject(event.target.value)}
            />
          </label>

          <label className="field">
            <span>{t('campaigns.segment')}</span>
            <select
              className="select"
              value={segmentId}
              onChange={(event) => setSegmentId(event.target.value)}
            >
              <option value="">{t('campaigns.useList')}</option>
              {(segments.data?.segments ?? []).map((segment) => (
                <option key={segment.id} value={segment.id}>
                  {segment.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {segmentId === '' && (
          <label className="field">
            <span>{t('campaigns.addresses')}</span>
            <textarea
              rows={3}
              value={addresses}
              onChange={(event) => setAddresses(event.target.value)}
            />
          </label>
        )}

        <label className="field">
          <span>{t('campaigns.body')}</span>
          <textarea rows={6} value={body} onChange={(event) => setBody(event.target.value)} />
          <span className="muted small">{t('campaigns.bodyHint')}</span>
        </label>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={
            name.trim() === '' ||
            subject.trim() === '' ||
            body.trim() === '' ||
            (segmentId === '' && recipients.length === 0) ||
            create.isPending
          }
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('campaigns.createCampaign')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="campaigns"
          rows={campaigns.data?.campaigns}
          columns={columns}
          rowKey={(campaign) => campaign.id}
          isLoading={campaigns.isLoading}
          error={campaigns.error}
          onRetry={() => void campaigns.refetch()}
          empty={{ title: t('campaigns.none'), description: t('campaigns.noneBody') }}
        />
      </Panel>
    </>
  );
}

function SegmentsTab(): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState<'CONTACT' | 'LEAD'>('CONTACT');
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});

  const segments = useQuery({ queryKey: ['segments'], queryFn: listSegments });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['segments'] });
  };

  const create = useMutation({
    mutationFn: () => createSegment({ name: name.trim(), entityType }),
    onSuccess: () => {
      setName('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const count = useMutation({
    mutationFn: (id: string) => previewSegment(id),
    onSuccess: (data) => setCounts((current) => ({ ...current, [data.segment.id]: data.members })),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSegment(id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<SegmentView>[] = [
    {
      key: 'name',
      header: t('campaigns.segment'),
      render: (segment) => (
        <span className="cell-stack">
          <span className="strong">{segment.name}</span>
          <span className="muted small">{t(`campaigns.entity.${segment.entity_type}`)}</span>
        </span>
      ),
    },
    {
      key: 'members',
      header: t('campaigns.members'),
      align: 'end',
      render: (segment) => (counts[segment.id] === undefined ? '—' : String(counts[segment.id])),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (segment) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => count.mutate(segment.id)}
          >
            {t('campaigns.count')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={remove.isPending}
            onClick={() => remove.mutate(segment.id)}
          >
            {t('campaigns.remove')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <Panel title={t('campaigns.newSegment')}>
        <p className="muted small">{t('campaigns.segmentHint')}</p>

        <div className="form-grid">
          <label className="field">
            <span>{t('campaigns.segmentName')}</span>
            <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </label>

          <label className="field">
            <span>{t('campaigns.entityType')}</span>
            <select
              className="select"
              value={entityType}
              onChange={(event) => setEntityType(event.target.value as 'CONTACT' | 'LEAD')}
            >
              <option value="CONTACT">{t('campaigns.entity.CONTACT')}</option>
              <option value="LEAD">{t('campaigns.entity.LEAD')}</option>
            </select>
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={name.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('campaigns.createSegment')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="campaigns-2"
          rows={segments.data?.segments}
          columns={columns}
          rowKey={(segment) => segment.id}
          isLoading={segments.isLoading}
          error={segments.error}
          onRetry={() => void segments.refetch()}
          empty={{ title: t('campaigns.noSegments'), description: t('campaigns.noSegmentsBody') }}
        />
      </Panel>
    </>
  );
}
